import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../config";
// Shares TeacherDetailModal's stylesheet on purpose — same "read-only
// detail popup" family, same theme, so the Academic Directory doesn't grow
// two slightly-different-looking modals for what's conceptually one
// pattern (click a chip → see where/when/who).
import s from "./TeacherDetailModal.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
function photoSrc(photoUrl) {
  if (!photoUrl) return null;
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${API_BASE}${photoUrl.startsWith("/") ? "" : "/"}${photoUrl}`;
}
function splitSchedule(raw) {
  if (!raw) return { days: "", time: "" };
  const idx = raw.lastIndexOf(" ");
  if (idx === -1) return { days: raw, time: "" };
  return { days: raw.slice(0, idx), time: raw.slice(idx + 1) };
}

/**
 * Sibling of TeacherDetailModal, keyed by Subject instead of Employee —
 * "where and when is this subject taught, and by whom". Read-only, same
 * stacking tier (z-index 1090 via the shared stylesheet) so it layers
 * correctly on top of the Academic Directory's own Section modal.
 *
 * Props:
 *   open         boolean
 *   subject      Subject row (must have id, title; ideally code, units)
 *   offerings    optional Array<ClassOffering> — every offering, not
 *                pre-filtered; this component does its own subjectId +
 *                term filtering so callers can pass the same array they
 *                already loaded for everything else on the page.
 *   teachers     optional Array<Employee>
 *   sections     optional Array<Section>
 *   gradeLevels  optional Array<GradeLevel>
 *   semesters    optional Array<Semester> — needed if activeSyId is used
 *   activeSyId   optional number — when set, only offerings whose
 *                semester is in this SY
 *   activeSemId  optional number — when set, only offerings in this
 *                exact semester
 *   onTeacherClick optional fn(teacherId) — lets the caller open the
 *                teacher's own detail modal on top of this one when a
 *                row's teacher name is clicked. Omit to render the name
 *                as plain (non-interactive) text.
 *   onClose      fn()
 */
export default function SubjectDetailModal({
  open,
  subject,
  offerings   = [],
  teachers    = [],
  sections    = [],
  gradeLevels = [],
  semesters   = [],
  activeSyId  = null,
  activeSemId = null,
  onTeacherClick,
  onClose,
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Resolve current-term offerings of this subject, across every section
  // that teaches it — mirrors TeacherDetailModal's myOfferings logic
  // exactly, just keyed by subjectId instead of teacherStaffId.
  const subjectOfferings = useMemo(() => {
    if (!subject?.id) return [];
    const semesterToSy = new Map(semesters.map(sm => [sm.id, sm.schoolYearId]));
    return (offerings || []).filter(o => {
      if (String(o.subjectId) !== String(subject.id)) return false;
      if (activeSemId && String(o.semesterId) !== String(activeSemId)) return false;
      if (activeSyId && !activeSemId) {
        if (String(semesterToSy.get(o.semesterId)) !== String(activeSyId)) return false;
      }
      return true;
    });
  }, [subject?.id, offerings, semesters, activeSyId, activeSemId]);

  const teachersById = useMemo(() => Object.fromEntries(teachers.map(x => [x.id, x])), [teachers]);
  const sectionsById  = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const gradeById     = useMemo(() => Object.fromEntries(gradeLevels.map(x => [x.id, x])), [gradeLevels]);

  if (!open || !subject) return null;

  const name = subject.title || "(Untitled subject)";
  const subtitle = [subject.code, subject.units != null ? `${subject.units} units` : null]
    .filter(Boolean).join(" · ");

  return createPortal(
    <div className={s.backdrop}>
      <div
        className={s.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sdm-title">
        <div className={s.head}>
          <div
            className={s.avatar}
            style={{ backgroundColor: avatarColor(subject.code || name) }}
            aria-hidden="true">
            {(subject.code || initials(name)).slice(0, 4).toUpperCase()}
          </div>
          <div className={s.headMain}>
            <div className={s.eyebrow}>Subject</div>
            <h3 id="sdm-title" className={s.title}>{name}</h3>
            {subtitle && <div className={s.subtitle}>{subtitle}</div>}
          </div>
          <button
            type="button"
            className={s.closeBtn}
            onClick={onClose}
            aria-label="Close">
            ✕
          </button>
        </div>

        <div className={s.body}>
          <div className={s.section}>
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>Taught in</span>
              <span className={s.sectionSub}>
                {subjectOfferings.length} class{subjectOfferings.length === 1 ? "" : "es"}
              </span>
            </div>
            {subjectOfferings.length === 0 ? (
              <div className={s.empty}>
                Not scheduled in any section this term.
              </div>
            ) : (
              <ul className={s.classList}>
                {subjectOfferings.map(o => {
                  const teacher = teachersById[o.teacherStaffId];
                  const sec     = sectionsById[o.sectionId];
                  const grade   = sec?.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
                  const { days } = splitSchedule(o.schedule);
                  const photo = photoSrc(teacher?.photoUrl);
                  return (
                    <li key={o.id} className={s.classRow}>
                      <div
                        className={s.rowAvatar}
                        style={photo
                          ? { backgroundImage: `url(${photo})` }
                          : { backgroundColor: avatarColor(teacher?.name || "?") }}
                        aria-hidden="true">
                        {!photo && initials(teacher?.name)}
                      </div>
                      <div className={s.classMain}>
                        <div className={s.classTitle}>
                          {sec ? (grade ? `${grade.name} · ${sec.name}` : sec.name) : "—"}
                        </div>
                        <div className={s.classMeta}>
                          {teacher ? (
                            onTeacherClick ? (
                              <button
                                type="button"
                                className={s.linkBtn}
                                onClick={() => onTeacherClick(teacher.id)}>
                                {teacher.name}
                              </button>
                            ) : teacher.name
                          ) : "No teacher assigned"}
                        </div>
                      </div>
                      <div className={s.classDays}>{days || "—"}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className={s.foot}>
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
